import React, { useEffect, useState } from "react";
import { useContent } from "@/lib/content";

/**
 * PlacesLogo — "places" wordmark with a cambiante hexagonal glyph that cycles
 * through 6 irregular shapes and 3 colors (orange → green → yellow → repeat).
 * The velocity is CMS-configurable via `logo_config.speed_ms` in `home_sections`.
 *
 * Fixed white typography, only the glyph animates.
 */

// 6 irregular hexagonal-ish shapes — SVG paths inside a 20×20 viewbox
const SHAPES = [
  // 1 — classic hex, slightly stretched
  "M10 1.5 L18 6 L18 14 L10 18.5 L2 14 L2 6 Z",
  // 2 — asymmetric hex, top-right tilted
  "M11 1 L18 4 L19 12 L12 19 L4 17 L1 9 Z",
  // 3 — elongated horizontally
  "M4 3 L15 2 L19 9 L17 16 L6 18 L1 11 Z",
  // 4 — narrower, top-heavy
  "M9 1 L16 5 L18 12 L12 19 L4 17 L2 8 Z",
  // 5 — squarer / plot-of-land
  "M3 3 L14 2 L18 7 L18 15 L10 19 L2 14 Z",
  // 6 — rotated tetragon-hex
  "M10 1 L17 3 L19 10 L15 18 L5 18 L1 9 Z",
];

const DEFAULT_COLORS = ["#FF5E00", "#00B247", "#FFD400"]; // orange · green · yellow

export default function PlacesLogo({ colorMode = "light", size = "text-3xl", className = "", speedMs, colors }) {
  const { home_sections = {} } = useContent();
  const cfg = home_sections.logo_config || {};
  const speed = Math.max(400, Number(speedMs || cfg.speed_ms) || 1600);
  const palette = Array.isArray(colors) && colors.length > 0 ? colors : DEFAULT_COLORS;
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % 6), speed);
    return () => clearInterval(t);
  }, [speed]);

  const shape = SHAPES[idx];
  const color = palette[idx % palette.length];
  const textColor = colorMode === "light" ? "text-black" : "text-white";

  return (
    <span className={`font-display tracking-tighter inline-flex items-baseline ${size} ${textColor} ${className}`} data-testid="places-logo">
      places
      <svg viewBox="0 0 20 20" width="0.65em" height="0.65em" className="inline-block ml-[0.05em] shrink-0" style={{ transform: "translateY(0.05em)" }} aria-hidden>
        <path d={shape} fill={color}>
          <animate attributeName="opacity" values="0.4;1" dur="0.35s" fill="freeze" begin="0s" />
        </path>
      </svg>
    </span>
  );
}
